import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { PortalUser, LoginResponse } from '../../shared/models/portal.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUser$ = new BehaviorSubject<PortalUser | null>(null);
  private apiUrl = environment.portalApiUrl;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
  ) {
    this.loadUserFromCookie();
  }

  get user$(): Observable<PortalUser | null> {
    return this.currentUser$.asObservable();
  }

  get currentUser(): PortalUser | null {
    return this.currentUser$.value;
  }

  get isLoggedIn(): boolean {
    return !!this.currentUser;
  }

  get isGlobalAdmin(): boolean {
    return this.currentUser?.isGlobalAdmin || false;
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/api/auth/login`, { email, password }).pipe(
      tap(response => {
        if (response.success) {
          // Store portal session cookies (same pattern as existing apps)
          const opts = { path: '/', domain: environment.cookieDomain };
          this.cookieService.set('portalTokenId', String(response.tokenId), opts);
          this.cookieService.set('portalTokenHash', response.tokenHash, opts);
          this.cookieService.set('portalUser', JSON.stringify(response.user), opts);

          this.currentUser$.next(response.user);
        }
      })
    );
  }

  logout(): void {
    this.http.post(`${this.apiUrl}/api/auth/logout`, {}).subscribe({ error: () => {} });
    this.cookieService.deleteAll('/', environment.cookieDomain);
    this.cookieService.deleteAll('/');
    this.currentUser$.next(null);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/forgot-password`, { email });
  }

  changePassword(token: string, oldPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/change-password`, { token, oldPassword, newPassword });
  }

  private loadUserFromCookie(): void {
    const userJson = this.cookieService.get('portalUser');
    if (userJson) {
      try {
        this.currentUser$.next(JSON.parse(userJson));
      } catch { /* invalid cookie */ }
    }
  }
}
